import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import { decideInitialSlide } from '../../../../app/functions';
import SliderArrow from '../../shared/slider_arrow';
import Tag from '../../shared/tag';
import './index.scss';

const TagsSlider = forwardRef(({
    selectedTags,
    availableTags,
    onTagSelect,
    onTagRemove,
    customSliderSettings = {},
    reinitializeOnDataChange = true,
}, slickRef) => {
    const location = useLocation();

    const deviceState = useSelector((store) => store.state.deviceState);

    if (slickRef === null) {
        slickRef = useRef(null);
    }

    const sharedSliderSettings = {
        rtl: true,
        infinite: false,
        slidesToScroll: 1,
        nextArrow: <SliderArrow />,
        prevArrow: <SliderArrow />,
        ...customSliderSettings?.shared,
    };

    const firstSelectedTag = selectedTags.length > 0 ? Math.min(...selectedTags) : 0;

    const getSliderSettings = () => {
        switch (deviceState) {
            case 'mobile':
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.mobile,
                    initialSlide: decideInitialSlide(
                        availableTags, customSliderSettings?.mobile?.slidesToShow ?? 3, firstSelectedTag
                    ),
                    slidesToShow: customSliderSettings?.mobile?.slidesToShow ?? 3,
                };

            case 'tablet':
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.tablet,
                    initialSlide: decideInitialSlide(
                        availableTags, customSliderSettings?.tablet?.slidesToShow ?? 3, firstSelectedTag
                    ),
                    slidesToShow: customSliderSettings?.tablet?.slidesToShow ?? 3,
                };

            case 'desktop':
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.desktop,
                    initialSlide: decideInitialSlide(
                        availableTags, customSliderSettings?.desktop?.slidesToShow ?? 4, firstSelectedTag
                    ),
                    slidesToShow: customSliderSettings?.desktop?.slidesToShow ?? 4,
                };

            default:
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.desktopLarge,
                    initialSlide: decideInitialSlide(
                        availableTags, customSliderSettings?.desktopLarge?.slidesToShow ?? 6, firstSelectedTag
                    ),
                    slidesToShow: customSliderSettings?.desktopLarge?.slidesToShow ?? 6,
                };
        }
    };

    const [sliderSettings, setSliderSettings] = useState(getSliderSettings);

    const handleTagClick = (tagId) => {
        if (selectedTags.includes(tagId)) {
            onTagRemove(tagId);
        } else {
            onTagSelect(tagId);
        }
    };

    const effectInputsList = [location.pathname, deviceState];

    if (reinitializeOnDataChange) {
        effectInputsList.push(availableTags);
    }

    // Update slider settings if the location path / device type / available tags change
    useEffect(() => {
        const newSliderSettings = getSliderSettings();
        setSliderSettings(newSliderSettings);

        // Return the slider to initial state whenever the state changes
        setTimeout(() => {
            if (slickRef.current) {
                slickRef.current.slickGoTo(newSliderSettings.initialSlide);
            }
        }, 1000);
    }, effectInputsList);

    return (
        <div className="slider-tags-select">
            <Slider className="tags-slider" ref={slickRef} {...sliderSettings}>
                {availableTags.map((tagData) => tagData && (
                    <div key={tagData.id} className="tag-container">
                        <Tag
                            data={tagData}
                            selectable
                            isSelected={selectedTags.includes(tagData.id)}
                            onTagClick={handleTagClick}
                        />
                    </div>
                ))}
            </Slider>
        </div>
    );
});

export default TagsSlider;
